import { ref, deleteObject } from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { API_ENDPOINTS } from '../config/apiConfig';

// Function to save crop settings to the server
export const saveCropSettings = async (tshirtUrl, rotation, cropInfo, sessionId, quiltDimensions, setIsProcessingImages, setUploadedImages, setShowWarning) => {
    if (!sessionId || !tshirtUrl) {
        console.warn("Missing sessionId or tshirtUrl, cannot save crop settings");
        return;
    }
    
    try {
        setIsProcessingImages(true);
        
        // Verify we're using the original image URL, not a tile URL
        if (tshirtUrl.includes('_tile')) {
            console.warn("Warning: URL appears to be a tile URL, not the original image URL:", tshirtUrl);
        }
        
        // Log the exact cropInfo being sent to the server
        console.log("Sending crop info to server:", {
            tshirtUrl,
            rotation,
            cropInfo
        });
        
        // Create FormData for multipart/form-data request
        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('tshirt_url', tshirtUrl);
        formData.append('rotation', rotation.toString());
        formData.append('adjusted', 'true');
        
        // Add crop information - using percentage values from the frontend
        if (cropInfo) {
            formData.append('crop_x', cropInfo.x.toString());
            formData.append('crop_y', cropInfo.y.toString());
            formData.append('crop_width', cropInfo.width.toString());
            formData.append('crop_height', cropInfo.height.toString());
        }
        
        // Let the backend retrieve the tile dimensions based on the session ID
        // The backend already stores quilt dimensions and can access them directly
        
        // Append session_id which the backend will use to lookup dimensions
        // This is more reliable than calculating dimensions in the frontend
        console.log("Letting backend determine tile dimensions based on session: " + sessionId);
        
        // If we have frontend dimensions, log them for debugging purposes only
        if (quiltDimensions && (quiltDimensions.tileWidthPixels || quiltDimensions.tileWidth)) {
            console.log("Frontend dimensions for reference:", {
                storedPixels: {
                    width: quiltDimensions.tileWidthPixels,
                    height: quiltDimensions.tileHeightPixels
                },
                physicalInches: {
                    width: quiltDimensions.tileWidth,
                    height: quiltDimensions.tileHeight
                }
            });
        }
        
        // Use the POST method with FormData
        const response = await fetch(API_ENDPOINTS.PROCESS_TSHIRT, {
            method: 'POST',
            body: formData,
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error("Server response:", errorText);
            throw new Error(`Failed to save crop settings: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Update the image in the uploadedImages array
        setUploadedImages(prevImages => {
            return prevImages.map(img => {
                // Match by original URL if we're editing the original image
                if (img.originalUrl === tshirtUrl || 
                    img.originalTshirtUrl === tshirtUrl || 
                    img.url === tshirtUrl) {
                    return {
                        ...img,
                        url: data.tile_url, // Update with new tile URL
                        tileUrl: data.tile_url,
                        dominantColor: data.dominant_color,
                        processed: true,
                        rotation: rotation,
                        crop: cropInfo,
                        adjusted: true,
                        // Store the original URLs to ensure we have them for future edits
                        originalUrl: img.originalUrl || tshirtUrl,
                        originalTshirtUrl: img.originalTshirtUrl || tshirtUrl
                    };
                }
                return img;
            });
        });
        
        setShowWarning('Image adjustments saved successfully.');
        setTimeout(() => setShowWarning(''), 3000);
        
    } catch (error) {
        console.error('Error saving crop settings:', error);
        setShowWarning(`Error: ${error.message}`);
    } finally {
        setIsProcessingImages(false);
    }
};

// Function to handle image deletion
export const handleDeleteImage = async (index, uploadedImages, setUploadedImages, currentUser, storage, db, setShowWarning) => {
    const imageToDelete = uploadedImages[index];

    if (!imageToDelete) {
        console.warn("Image not found at the specified index.");
        return;
    }

    try {
        // First, update UI immediately to provide feedback
        setUploadedImages(prevImages => prevImages.filter((_, i) => i !== index));
        console.log(`Removed image at index ${index} from UI`);

        // Step 1: Access Firestore to find and delete associated tile and image data
        if (currentUser && currentUser.uid) {
            const sessionRef = doc(db, 'sessions', currentUser.uid);
            const sessionSnap = await getDoc(sessionRef);

            if (sessionSnap.exists()) {
                const sessionData = sessionSnap.data();
                let needsUpdate = false;
                let updatedSessionData = {...sessionData};

                // Check if tile_list exists and is valid
                const tileList = sessionData.tile_list;
                if (tileList && Array.isArray(tileList)) {
                    console.log("Found tile_list with", tileList.length, "entries");
                    
                    // Process tile_list identically to uploadedImages, with the same robust matching strategy
                    // Create helper functions to get unique identifiers for a tile
                    const getTileIdentifier = (tile) => {
                        if (!tile) return '';
                        
                        // Try tshirt_url first
                        if (tile.tshirt_url) {
                            const parts = tile.tshirt_url.split('/');
                            const filename = parts[parts.length - 1];
                            return filename;
                        }
                        
                        // Try url as fallback
                        if (tile.url) {
                            const parts = tile.url.split('/');
                            const filename = parts[parts.length - 1];
                            return filename;
                        }
                        
                        return '';
                    };
                    
                    // Get the identifiers for the image being deleted
                    const imageIdentifier = imageToDelete.url ? imageToDelete.url.split('/').pop() : '';
                    const imagePath = imageToDelete.path ? imageToDelete.path.split('/').pop() : '';
                    const originalName = imageToDelete.originalName || '';
                    
                    console.log("Looking to remove tile with identifiers:", {
                        imageIdentifier,
                        imagePath,
                        originalName
                    });
                    
                    // Filter out matching tiles using the same comprehensive strategy as for uploadedImages
                    const initialTileCount = tileList.length;
                    const updatedTileList = tileList.filter((tile, idx) => {
                        if (!tile) return false;
                        
                        // 1. Index-based matching
                        const indexMatch = idx === index;
                        
                        // 2. Direct URL matching
                        const urlMatch = 
                            (tile.tshirt_url && imageToDelete.url && tile.tshirt_url === imageToDelete.url) ||
                            (tile.url && imageToDelete.url && tile.url === imageToDelete.url);
                        
                        // 3. Filename/identifier matching for more fuzzy matching
                        const tileIdentifier = getTileIdentifier(tile);
                        const identifierMatch = 
                            (imageIdentifier && tileIdentifier && tileIdentifier.includes(imageIdentifier)) || 
                            (imagePath && tileIdentifier && tileIdentifier.includes(imagePath));
                        
                        // 4. Original name matching if available
                        const originalNameMatch = 
                            originalName && 
                            tile.originalName && 
                            tile.originalName === originalName;
                        
                        // Remove if ANY match criteria are true
                        const shouldRemove = 
                            indexMatch || 
                            urlMatch || 
                            identifierMatch || 
                            originalNameMatch;
                        
                        if (shouldRemove) {
                            console.log("Found matching tile to remove:", tile.tshirt_url || tile.url || "unknown URL");
                            
                            // Try to delete the tile from Firebase Storage
                            try {
                                const tileUrl = tile.url || tile.tshirt_url;
                                if (tileUrl) {
                                    const tilePath = tileUrl.split(".appspot.com/")[1];
                                    if (tilePath) {
                                        const decodedTilePath = decodeURIComponent(tilePath);
                                        console.log("Deleting tile from Storage:", decodedTilePath);
                                        const tileRef = ref(storage, decodedTilePath);
                                        deleteObject(tileRef).catch(error => 
                                            console.log("Error deleting tile from Storage:", error.message)
                                        );
                                    }
                                }
                            } catch (error) {
                                console.log("Error preparing tile deletion:", error.message);
                            }
                        }
                        
                        return !shouldRemove; // Keep only if doesn't match our deletion criteria
                    });
                    
                    const finalTileCount = updatedTileList.length;
                    console.log(`Processed tile_list. Before: ${initialTileCount}, After: ${finalTileCount}`);
                    
                    if (initialTileCount !== finalTileCount) {
                        updatedSessionData.tile_list = updatedTileList;
                        updatedSessionData.total_tiles_uploaded = updatedTileList.length;
                        needsUpdate = true;
                    } else {
                        console.warn("⚠️ Warning: No tiles were removed from tile_list - using fallback");
                        
                        // Forced removal by index as a fallback if nothing else worked
                        if (tileList.length > index) {
                            console.log("Falling back to forced index-based removal for tile_list");
                            updatedSessionData.tile_list = [
                                ...tileList.slice(0, index),
                                ...tileList.slice(index + 1)
                            ];
                            updatedSessionData.total_tiles_uploaded = updatedSessionData.tile_list.length;
                            needsUpdate = true;
                        }
                    }
                } else {
                    console.log("No valid tile_list found in session data");
                }
                
                // More thorough cleaning of uploadedImages array in Firestore
                if (sessionData.uploadedImages && Array.isArray(sessionData.uploadedImages)) {
                    // Create helper functions to get unique identifiers for an image
                    const getImageIdentifier = (img) => {
                        if (!img) return '';
                        
                        // Try url first
                        if (img.url) {
                            const parts = img.url.split('/');
                            const filename = parts[parts.length - 1];
                            return filename;
                        }
                        
                        // Try path as fallback
                        if (img.path) {
                            const parts = img.path.split('/');
                            const filename = parts[parts.length - 1];
                            return filename;
                        }
                        
                        return '';
                    };
                    
                    const getOriginalFileName = (img) => {
                        return img?.originalName || '';
                    };
                    
                    const getTShirtUrlIdentifier = (img) => {
                        if (!img || !img.tshirtUrl) return '';
                        const parts = img.tshirtUrl.split('/');
                        return parts[parts.length - 1];
                    };
                    
                    const getOriginalUrlIdentifier = (img) => {
                        if (!img || !img.originalUrl) return '';
                        const parts = img.originalUrl.split('/');
                        return parts[parts.length - 1];
                    };
                    
                    // Get all possible identifiers for the image being deleted
                    const imageIdentifier = getImageIdentifier(imageToDelete);
                    const originalName = getOriginalFileName(imageToDelete);
                    const tshirtUrlId = getTShirtUrlIdentifier(imageToDelete);
                    const originalUrlId = getOriginalUrlIdentifier(imageToDelete);
                    
                    console.log("Looking to remove image with identifiers:", {
                        imageIdentifier,
                        originalName,
                        tshirtUrlId,
                        originalUrlId
                    });
                    
                    // Filter out the image using multiple matching strategies
                    const initialCount = sessionData.uploadedImages.length;
                    updatedSessionData.uploadedImages = sessionData.uploadedImages.filter((img, idx) => {
                        if (!img) return false;
                        
                        // 1. Index-based matching
                        const indexMatch = idx === index;
                        
                        // 2. Direct URL matching
                        const urlMatch = img.url === imageToDelete.url;
                        
                        // 3. Original name matching
                        const originalNameMatch = 
                            originalName && 
                            img.originalName && 
                            img.originalName === originalName;
                        
                        // 4. tshirtUrl matching
                        const tshirtUrlMatch = 
                            imageToDelete.tshirtUrl && 
                            img.tshirtUrl && 
                            img.tshirtUrl === imageToDelete.tshirtUrl;
                        
                        // 5. originalUrl matching
                        const originalUrlMatch = 
                            imageToDelete.originalUrl && 
                            img.originalUrl && 
                            img.originalUrl === imageToDelete.originalUrl;
                        
                        // 6. Filename/identifier matching
                        const imgIdentifier = getImageIdentifier(img);
                        const identifierMatch = imageIdentifier && imgIdentifier && imgIdentifier.includes(imageIdentifier);
                        
                        // 7. TShirtUrl identifier matching
                        const imgTShirtId = getTShirtUrlIdentifier(img);
                        const tshirtIdMatch = tshirtUrlId && imgTShirtId && (
                            imgTShirtId.includes(tshirtUrlId) || 
                            tshirtUrlId.includes(imgTShirtId)
                        );
                        
                        // 8. OriginalUrl identifier matching
                        const imgOriginalId = getOriginalUrlIdentifier(img);
                        const originalIdMatch = originalUrlId && imgOriginalId && (
                            imgOriginalId.includes(originalUrlId) || 
                            originalUrlId.includes(imgOriginalId)
                        );
                        
                        // Remove if ANY match criteria are true
                        const shouldRemove = 
                            indexMatch || 
                            urlMatch || 
                            originalNameMatch || 
                            tshirtUrlMatch || 
                            originalUrlMatch || 
                            identifierMatch || 
                            tshirtIdMatch || 
                            originalIdMatch;
                        
                        if (shouldRemove) {
                            console.log("Found matching image to remove from uploadedImages:", img.url || img.originalUrl || img.tshirtUrl || "unknown URL");
                        }
                        
                        return !shouldRemove; // Keep only if doesn't match our deletion criteria
                    });
                    
                    const finalCount = updatedSessionData.uploadedImages.length;
                    console.log(`Removed image from Firestore uploadedImages. Before: ${initialCount}, After: ${finalCount}`);
                    
                    if (initialCount !== finalCount) {
                        needsUpdate = true;
                    } else {
                        console.warn("⚠️ Warning: No images were removed from uploadedImages array - possible mismatch!");
                        
                        // Forced removal by index as a fallback if nothing else worked
                        if (sessionData.uploadedImages.length > index) {
                            console.log("Falling back to forced index-based removal");
                            updatedSessionData.uploadedImages = [
                                ...sessionData.uploadedImages.slice(0, index),
                                ...sessionData.uploadedImages.slice(index + 1)
                            ];
                            needsUpdate = true;
                        }
                    }
                }
                
                // Update Firestore document if changes were made
                if (needsUpdate) {
                    try {
                        await updateDoc(sessionRef, updatedSessionData);
                        console.log("Updated Firestore document successfully");
                    } catch (updateError) {
                        console.error("Error updating Firestore document:", updateError);
                        
                        // Try a more focused update with just the uploadedImages and tile_list
                        try {
                            await updateDoc(sessionRef, {
                                uploadedImages: updatedSessionData.uploadedImages,
                                tile_list: updatedSessionData.tile_list || [],
                                total_tiles_uploaded: updatedSessionData.total_tiles_uploaded || 0
                            });
                            console.log("Updated Firestore document with focused update");
                        } catch (focusedUpdateError) {
                            console.error("Even focused update failed:", focusedUpdateError);
                        }
                    }
                } else {
                    console.log("No changes needed to Firestore document structure");
                    
                    // Even if needsUpdate is false, make sure uploadedImages is updated
                    try {
                        const currentUploadedImages = [...uploadedImages].filter((_, i) => i !== index);
                        await updateDoc(sessionRef, { uploadedImages: currentUploadedImages });
                        console.log("Updated uploadedImages in Firestore directly");
                    } catch (e) {
                        console.error("Error updating only uploadedImages:", e);
                    }
                }
                
                // Delete the original T-shirt image from Firebase Storage
                if (imageToDelete.path) {
                    try {
                        console.log("Deleting T-shirt image:", imageToDelete.path);
                        const storageRef = ref(storage, imageToDelete.path);
                        await deleteObject(storageRef);
                    } catch (error) {
                        console.log("Error deleting T-shirt image:", error.message);
                    }
                }
                
                // Also try to delete by originalUrl if available
                if (imageToDelete.originalUrl) {
                    try {
                        const originalPath = imageToDelete.originalUrl.split(".appspot.com/")[1];
                        if (originalPath) {
                            const decodedPath = decodeURIComponent(originalPath);
                            console.log("Deleting original image:", decodedPath);
                            const originalRef = ref(storage, decodedPath);
                            await deleteObject(originalRef);
                        }
                    } catch (error) {
                        console.log("Error deleting original image:", error.message);
                    }
                }
            } else {
                console.log("No session data found in Firestore");
            }
        }
        
        // Show success feedback
        setShowWarning("Image successfully deleted");
        setTimeout(() => setShowWarning(''), 2000);
        
        // Update session data with a direct set operation to ensure the uploadedImages array is updated
        if (currentUser && currentUser.uid) {
            try {
                // Get the current state AFTER the UI update
                const currentUploadedImages = [...uploadedImages].filter((_, i) => i !== index);
                
                // Directly set the uploadedImages in Firestore
                const directUpdateRef = doc(db, 'sessions', currentUser.uid);
                await updateDoc(directUpdateRef, {
                    uploadedImages: currentUploadedImages
                });
                console.log("Performed direct uploadedImages update for extra safety");
            } catch (directUpdateError) {
                console.error("Error during direct uploadedImages update:", directUpdateError);
            }
        }
    } catch (error) {
        console.error("Error during image deletion:", error);
        setShowWarning("Error deleting image. Some resources may not have been properly removed.");
    }
};

// Function to handle image upload completion
export const handleImageUploaded = (newImage, setUploadedImages, setShowWarning, setProcessingError) => {
    // If this is a removal request (from error handling in EnhancedImageUpload)
    if (newImage.action === 'remove') {
        setUploadedImages(prev => prev.filter(img => 
            img.placeholderUrl !== newImage.placeholderUrl &&
            img.placeholderKey !== newImage.placeholderUrl
        ));
        return;
    }
    
    // If this is a final processed image that should replace a temporary one
    if (newImage.placeholderKey) {
        setUploadedImages(prev => {
            // Find and replace the temporary image
            return prev.map(img => {
                if (img.placeholderUrl === newImage.placeholderKey || 
                    img.isLocalPreview && img.originalName === newImage.originalName) {
                    return newImage; // Replace with processed image
                }
                return img;
            });
        });
    } 
    // If this is a new image (either temporary or direct final)
    else {
        setUploadedImages(prev => [...prev, newImage]);
    }
    
    // Clear any warnings
    setShowWarning('');
    setProcessingError(null);
};

// Function to wait for all tiles to be uploaded
export const waitForAllTilesToUpload = async (currentUser, db, requiredImages) => {
    const checkTileUploadStatus = async () => {
        const sessionId = currentUser.uid;
        const sessionRef = doc(db, 'sessions', sessionId);
        const sessionDoc = await getDoc(sessionRef);
    
        if (sessionDoc.exists()) {
            const sessionData = sessionDoc.data();
            const totalTilesUploaded = sessionData?.total_tiles_uploaded || 0;
            return totalTilesUploaded >= requiredImages;
        }
        return false;
    };

    const maxRetries = 10;
    let retries = 0;
    while (retries < maxRetries) {
        const allTilesUploaded = await checkTileUploadStatus();
        if (allTilesUploaded) return true;
        await new Promise((resolve) => setTimeout(resolve, 6000)); 
        retries += 1;
    }
    return false;
};

// Function to calculate required images based on grid size
export const calculateRequiredImages = (gridSize) => {
    if (!gridSize || !gridSize.includes('x')) {
        return 0;
    }
    
    // Extract columns and rows from gridSize string (e.g., "10x14")
    const [columns, rows] = gridSize.split('x').map(Number);
    
    // Handle predefined sizes first
    switch (gridSize) {
        case "4x4": return 16;
        case "4x5": return 20;
        case "4x6": return 24;
        case "4x7": return 28;
        case "5x5": return 25;
        case "5x6": return 30;
        case "5x7": return 35;
        case "6x6": return 36;
        case "6x7": return 42;
        case "6x8": return 48;
        case "7x7": return 49;
        case "7x8": return 56;
        case "8x8": return 64;
        case "8x9": return 72;
        default:
            // For custom sizes, calculate based on columns and rows
            if (isNaN(columns) || isNaN(rows)) {
                return 0; // Invalid format
            }
            return columns * rows;
    }
};
